import { useState } from 'react';
import { colour, space, text } from '../design/tokens.ts';
import { Button, Card, Field, Page, Select, Status, Tag, TextArea, TextInput } from '../components/basics.tsx';
import { CouldNotLoad, DoneToast, Loading, NoPermission } from '../components/states.tsx';
import { Dialog } from '../components/Dialog.tsx';
import {
  ProcessEditor,
  type ProcessRow,
  type ToolChoice
} from './admin/ProcessEditor.tsx';
import { UnrealSection } from './admin/Unreal.tsx';
import { api } from '../api/client.ts';
import { useLoad } from '../app/useLoad.ts';
import { useMe } from '../app/me.tsx';

/**
 * Screen 8: admin settings. Admins only — an operator cannot reach it either.
 *
 * Nothing here can show a credential, because nothing here has one: a storage
 * root or a connection holds `secret_ref`, the **name** of a secret in AWS
 * Secrets Manager (rule 11). The server refuses a value typed into that box,
 * and says so.
 */

type Section =
  | 'storage'
  | 'connections'
  | 'users'
  | 'machines'
  | 'unreal'
  | 'tools'
  | 'processes'
  | 'pipeline_defaults'
  | 'audit';

interface Root {
  _id: string;
  name: string;
  kind: string;
  secret_ref?: string;
  host?: string;
  share?: string;
  bucket?: string;
  account?: string;
  base_url?: string;
  status?: string;
  last_checked_at?: string;
}

interface Person {
  cognito_sub: string;
  name?: string;
  role: string;
  can_mark_good: boolean;
  says: string;
}

interface ToolPackage {
  key: string;
  sha256: string;
  bytes?: number;
  unpack?: 'zip' | 'tar.gz' | 'none';
}

interface ToolRow {
  name: string;
  label: string;
  install: 'pull' | 'manual';
  probe?: Record<string, string>;
  entry?: Record<string, string>;
  install_says?: string;
  package?: Record<string, ToolPackage>;
  /** Which steps would break if this went away. */
  used_by: string[];
}

interface MachineRow {
  host: string;
  online: boolean;
  retired?: boolean;
  paused?: boolean;
  queued: number;
  running: number;
  says: string;
  queue: string;
  runs_processes: string[];
  own_templates: string[];
  flagged: boolean;
  /** It has a token, so it can ask for credentials. */
  enrolled: boolean;
  revoked_at?: string;
}

export function Admin() {
  const me = useMe();
  const [section, setSection] = useState<Section>('storage');
  const [toast, setToast] = useState<string | null>(null);
  const [editingTool, setEditingTool] = useState<ToolRow | null>(null);
  const [editingProcess, setEditingProcess] = useState<ProcessRow | null>(null);
  const [inviting, setInviting] = useState<{
    email: string;
    name: string;
    role: string;
    can_mark_good: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [addingMachine, setAddingMachine] = useState<string | null>(null);
  const [enrolment, setEnrolment] = useState<{ host: string; key: string; expires_at: string } | null>(null);
  const [editingRoot, setEditingRoot] = useState<
    (Partial<Root> & { kind: string; credential: Record<string, string> }) | null
  >(null);

  const role = me.status === 'ready' ? me.answer.me.role : null;
  const allowed = me.status === 'ready' && me.answer.can.admin;

  const { state: page, reload: reloadPage } = useLoad<{
    sections: { key: string; says: string }[];
    about_secrets: string;
  }>(() => (allowed ? api.get('/api/admin') : Promise.reject(new Error('not an admin'))), [allowed]);

  const { state: roots, reload: reloadRoots } = useLoad<{ roots: Root[] }>(
    () => (allowed ? api.get('/api/roots') : Promise.resolve({ roots: [] })),
    [allowed]
  );
  const { state: connections, reload: reloadConnections } = useLoad<{
    connections: { name: string; label?: string; base_url?: string; secret_ref?: string; status?: string; last_checked_says?: string }[];
  }>(() => (allowed ? api.get('/api/connections') : Promise.resolve({ connections: [] })), [allowed]);
  const { state: users, reload: reloadUsers } = useLoad<{
    people: Person[];
    agents: Person[];
    roles: { key: string; says: string }[];
    about_marking_good: string;
    about_groups: string;
  }>(
    () =>
      allowed
        ? api.get('/api/users')
        : Promise.resolve({ people: [], agents: [], roles: [], about_marking_good: '', about_groups: '' }),
    [allowed]
  );
  const { state: machines, reload: reloadMachines } = useLoad<{
    machines: MachineRow[];
    processes: { name: string; label: string }[];
  }>(() => (allowed ? api.get('/api/admin/machines') : Promise.resolve({ machines: [], processes: [] })), [allowed]);
  const { state: toolList, reload: reloadTools } = useLoad<{
    tools: ToolRow[];
    platforms: string[];
    about: string;
    about_manual: string;
  }>(
    () =>
      allowed
        ? api.get('/api/admin/tools')
        : Promise.resolve({ tools: [], platforms: [], about: '', about_manual: '' }),
    [allowed]
  );

  const { state: processList, reload: reloadProcesses } = useLoad<{
    processes: ProcessRow[];
    tools: ToolChoice[];
    platforms: string[];
    placeholders: { name: string; says: string }[];
    about: string;
  }>(
    () =>
      allowed
        ? api.get('/api/admin/processes')
        : Promise.resolve({ processes: [], tools: [], platforms: [], placeholders: [], about: '' }),
    [allowed]
  );

  const { state: kinds } = useLoad<{
    kinds: { key: string; says: string; needs: { name: string; says: string; required: boolean }[] }[];
    credentials: Record<string, { name: string; says: string; secret: boolean; required: boolean }[]>;
    about: string;
    can_store: boolean;
  }>(
    () =>
      allowed
        ? api.get('/api/roots/kinds')
        : Promise.resolve({ kinds: [], credentials: {}, about: '', can_store: false }),
    [allowed]
  );

  const { state: defaults, reload: reloadDefaults } = useLoad<{
    pipeline_defaults: { on_input_change: string; rerun_cap: number; quiet_minutes_before_failed: number };
    confirm_above_runs: number;
    says: Record<string, string>;
  }>(
    () =>
      allowed
        ? api.get('/api/admin/defaults')
        : Promise.reject(new Error('not an admin')),
    [allowed]
  );
  const { state: audit } = useLoad<{
    entries: { at: string; actor: string; section: string; change: string }[];
    sections: string[];
  }>(() => (allowed ? api.get('/api/admin/audit') : Promise.resolve({ entries: [], sections: [] })), [allowed]);

  if (me.status === 'ready' && !allowed) {
    return (
      <Page eyebrow="MAESTRO · ADMIN SETTINGS" title="Admin settings">
        <NoPermission what="Admin settings" role={role ?? undefined} />
      </Page>
    );
  }

  const test = async (what: 'root' | 'connection', id: string): Promise<void> => {
    try {
      const result = await api.post<{ test: { says: string } }>(
        what === 'root' ? `/api/roots/${id}/test` : `/api/connections/${id}/test`
      );
      setToast(result.test.says);
      if (what === 'root') reloadRoots();
      else reloadConnections();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That test did not run.');
    }
  };

  const setAccess = async (sub: string, patch: Record<string, unknown>): Promise<void> => {
    try {
      await api.patch(`/api/users/${sub}`, patch);
      setToast('Changed, and written to the audit log.');
      reloadUsers();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That change was refused.');
    }
  };

  const setMachine = async (host: string, patch: Record<string, unknown>): Promise<void> => {
    try {
      await api.patch(`/api/admin/machines/${host}`, patch);
      setToast('Changed, and written to the audit log.');
      reloadMachines();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That change was refused.');
    }
  };

  const saveTool = async (tool: ToolRow): Promise<void> => {
    try {
      const { name, used_by, ...body } = tool;
      void used_by;
      await api.put(`/api/admin/tools/${encodeURIComponent(name)}`, body);
      setEditingTool(null);
      setToast('Saved, and written to the audit log.');
      reloadTools();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That tool was refused.');
    }
  };

  const removeTool = async (name: string): Promise<void> => {
    try {
      await api.del(`/api/admin/tools/${encodeURIComponent(name)}`);
      setToast(`${name} removed.`);
      reloadTools();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That tool could not be removed.');
    }
  };

  const saveProcess = async (def: ProcessRow): Promise<void> => {
    try {
      const { name, used_by, problems, sample, ...body } = def;
      void used_by;
      void problems;
      void sample;
      await api.put(`/api/admin/processes/${encodeURIComponent(name)}`, body);
      setEditingProcess(null);
      setToast('Saved, and written to the audit log.');
      reloadProcesses();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That process was refused.');
    }
  };

  const removeProcess = async (name: string): Promise<void> => {
    try {
      await api.del(`/api/admin/processes/${encodeURIComponent(name)}`);
      setToast(`${name} removed.`);
      reloadProcesses();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That process could not be removed.');
    }
  };

  const sendInvite = async (): Promise<void> => {
    if (!inviting) return;
    setBusy(true);
    try {
      const made = await api.post<{ person: { says: string } }>('/api/users', {
        email: inviting.email.trim(),
        ...(inviting.name.trim() ? { name: inviting.name.trim() } : {}),
        role: inviting.role,
        can_mark_good: inviting.can_mark_good
      });
      setInviting(null);
      setToast(made.person.says);
      reloadUsers();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That invitation was refused.');
    } finally {
      setBusy(false);
    }
  };

  const inviteAgain = async (sub: string): Promise<void> => {
    try {
      const out = await api.post<{ says: string }>(`/api/users/${sub}/invite-again`);
      setToast(out.says);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That could not be sent.');
    }
  };

  const saveRoot = async (): Promise<void> => {
    if (!editingRoot) return;
    setBusy(true);
    try {
      const { _id, credential, ...body } = editingRoot;
      const filled = Object.fromEntries(
        Object.entries(credential).filter(([, v]) => v.trim() !== '')
      );
      const payload = {
        ...body,
        ...(Object.keys(filled).length > 0 ? { credential: filled } : {})
      };
      if (_id) {
        const { kind, ...changes } = payload;
        void kind;
        await api.patch(`/api/roots/${_id}`, changes);
      } else {
        await api.post('/api/roots', payload);
      }
      setEditingRoot(null);
      setToast('Saved. The credential went to Secrets Manager, never the database.');
      reloadRoots();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That location was refused.');
    } finally {
      setBusy(false);
    }
  };

  const makeEnrolmentKey = async (): Promise<void> => {
    if (addingMachine === null) return;
    setBusy(true);
    try {
      const made = await api.post<{ enrolment: { host: string; key: string; expires_at: string } }>(
        '/api/admin/machines/enrolment-key',
        { host: addingMachine.trim() }
      );
      setAddingMachine(null);
      setEnrolment(made.enrolment);
      reloadMachines();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That machine could not be added.');
    } finally {
      setBusy(false);
    }
  };

  const revokeMachine = async (host: string): Promise<void> => {
    try {
      const out = await api.post<{ says: string }>(
        `/api/admin/machines/${encodeURIComponent(host)}/revoke`
      );
      setToast(out.says);
      reloadMachines();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That machine could not be revoked.');
    }
  };

  const setDefault = async (patch: Record<string, unknown>): Promise<void> => {
    try {
      await api.patch('/api/admin/defaults', patch);
      setToast('Saved, with what it was before.');
      reloadDefaults();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That value was refused.');
    }
  };

  return (
    <Page
      eyebrow="MAESTRO · ADMIN SETTINGS"
      title="Admin settings"
      description={page.data?.about_secrets}
    >
      <Card>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight }}>
          {(page.data?.sections ?? []).map((s) => (
            <Button key={s.key} tone={section === s.key ? 'primary' : 'plain'} onClick={() => setSection(s.key as Section)}>
              {s.says}
            </Button>
          ))}
        </div>
      </Card>

      {page.status === 'loading' && !page.data && <Loading says="Loading the settings…" rows={4} />}
      {page.status === 'failed' && !page.data && (
        <CouldNotLoad what="the settings" error={page.error} onRetry={reloadPage} />
      )}

      {section === 'storage' && (
        <Card aside={kinds.data?.about}>
          <span style={{ ...text.small, color: colour.inkSoft }}>
            Where files actually live. A location is a root and a relative path, never a drive
            letter, so the same record opens on any machine.
          </span>
          <Button
            tone="primary"
            onClick={() => setEditingRoot({ kind: 'fileshare', name: '', credential: {} })}
            disabled={kinds.data?.can_store === false}
            disabledReason="This Maestro has nowhere to keep a credential. It is running without AWS."
          >
            Add a location
          </Button>
        </Card>
      )}

      {section === 'storage' &&
        (roots.data?.roots ?? []).map((root) => (
          <Card key={root._id} title={root.name} aside={root.kind}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
              {root.host && <Tag tone="mono">{root.host}/{root.share}</Tag>}
              {root.bucket && <Tag tone="mono">{root.bucket}</Tag>}
              {root.account && <Tag tone="mono">{root.account}</Tag>}
              {root.base_url && <Tag tone="mono">{root.base_url}</Tag>}
              <Tag>secret name: {root.secret_ref ?? 'none set'}</Tag>
              {root.status && <Status value={root.status === 'ok' ? 'good' : root.status === 'failed' ? 'bad' : 'notmade'} />}
              <Button onClick={() => void test('root', root._id)}>Test connection</Button>
              <Button
                onClick={() =>
                  setEditingRoot({ ...root, kind: root.kind, credential: {} })
                }
              >
                Edit
              </Button>
            </div>
            {root.last_checked_at && (
              <span style={{ ...text.note, color: colour.inkMuted }}>
                last checked {new Date(root.last_checked_at).toLocaleString()}
              </span>
            )}
          </Card>
        ))}

      {section === 'connections' &&
        (connections.data?.connections ?? []).map((connection) => (
          <Card key={connection.name} title={connection.label ?? connection.name}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
              <Tag tone="mono">{connection.base_url ?? 'no address set'}</Tag>
              <Tag>secret name: {connection.secret_ref ?? 'none set'}</Tag>
              <Button onClick={() => void test('connection', connection.name)}>Test connection</Button>
            </div>
            {connection.last_checked_says && (
              <span style={{ ...text.note, color: colour.inkSoft }}>{connection.last_checked_says}</span>
            )}
          </Card>
        ))}

      {section === 'users' && (
        <>
          <Card aside={users.data?.about_marking_good}>
            <span style={{ ...text.small, color: colour.inkSoft }}>{users.data?.about_groups}</span>
            <span style={{ ...text.small, color: colour.inkSoft }}>
              Nobody can sign themselves up. Adding somebody here creates their account and emails
              them a temporary password; they choose their own the first time they arrive.
            </span>
            <Button
              tone="primary"
              onClick={() =>
                setInviting({ email: '', name: '', role: 'viewer', can_mark_good: false })
              }
            >
              Add somebody
            </Button>
          </Card>
          {(users.data?.people ?? []).map((person) => (
            <Card key={person.cognito_sub}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.control, alignItems: 'center' }}>
                <div style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={text.bodyStrong}>{person.name ?? person.cognito_sub}</span>
                  <span style={{ ...text.note, color: colour.inkSoft }}>{person.says}</span>
                </div>
                <Field label="Role">
                  <Select
                    value={person.role}
                    onChange={(role) => void setAccess(person.cognito_sub, { role })}
                    options={(users.data?.roles ?? []).map((r) => ({ value: r.key, label: r.says }))}
                  />
                </Field>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, ...text.small }}>
                  <input
                    type="checkbox"
                    checked={person.can_mark_good}
                    disabled={person.role === 'viewer'}
                    onChange={(e) => void setAccess(person.cognito_sub, { can_mark_good: e.target.checked })}
                    style={{ width: 20, height: 20 }}
                  />
                  Can mark results good
                </label>
                <Button onClick={() => void inviteAgain(person.cognito_sub)}>
                  Send the invitation again
                </Button>
              </div>
            </Card>
          ))}
          {(users.data?.agents ?? []).length > 0 && (
            <Card title="Agents" aside="A maestro_d on one machine. Not a person, and not given a role.">
              {(users.data?.agents ?? []).map((agent) => (
                <span key={agent.cognito_sub} style={{ ...text.small, color: colour.inkSoft }}>
                  {agent.says}
                </span>
              ))}
            </Card>
          )}
        </>
      )}

      {section === 'machines' && (
        <Card>
          <span style={{ ...text.small, color: colour.inkSoft }}>
            A machine enrols itself with a key you make here. It holds no AWS key and no password
            — it asks Maestro for credentials scoped to its own queue, good for an hour. Revoking
            one stops it asking; what it holds expires on its own.
          </span>
          <Button tone="primary" onClick={() => setAddingMachine('')}>
            Add a machine
          </Button>
        </Card>
      )}

      {section === 'machines' &&
        (machines.data?.machines ?? []).map((machine) => (
          <Card key={machine.host} title={machine.host} aside={machine.queue}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
              <Status value={machine.online ? 'good' : machine.retired ? 'superseded' : 'bad'} detail={machine.says} />
              {machine.flagged && <Status value="stale" detail="work is stuck behind it" />}
              <Tag>{machine.queued} queued · {machine.running} running</Tag>
              {machine.own_templates.length > 0 && (
                <Tag>own command for {machine.own_templates.join(', ')}</Tag>
              )}
              <Button onClick={() => void setMachine(machine.host, { paused: !machine.paused })}>
                {machine.paused ? 'Un-pause' : 'Pause'}
              </Button>
              {/* Three different things, deliberately. Pause sends it no new
                  work. Retire means the machine is gone. Revoke takes away
                  its credentials and keeps everything it did. */}
              {machine.enrolled && !machine.revoked_at && (
                <Button tone="danger" onClick={() => void revokeMachine(machine.host)}>
                  Revoke access
                </Button>
              )}
            </div>
            {machine.revoked_at && (
              <span style={{ ...text.note, color: colour.staleInk }}>
                Revoked {new Date(machine.revoked_at).toLocaleString()}. Add it again to bring it
                back — it will need a new enrolment key.
              </span>
            )}
            <span style={{ ...text.note, color: colour.inkMuted }}>
              Can run: {machine.runs_processes.join(', ') || 'nothing configured'}
            </span>
          </Card>
        ))}

      {section === 'tools' && (
        <>
          <Card aside={toolList.data?.about_manual}>
            <span style={{ ...text.small, color: colour.inkSoft }}>{toolList.data?.about}</span>
            <Button
              tone="primary"
              onClick={() =>
                setEditingTool({
                  name: '',
                  label: '',
                  install: 'manual',
                  probe: {},
                  package: {},
                  used_by: []
                })
              }
            >
              Add a tool
            </Button>
          </Card>

          {(toolList.data?.tools ?? []).map((tool) => (
            <Card key={tool.name} title={tool.label || tool.name} aside={tool.name}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}>
                <Status
                  value={tool.install === 'pull' ? 'good' : 'notmade'}
                  detail={
                    tool.install === 'pull'
                      ? 'Maestro ships it'
                      : 'installed by hand on each machine'
                  }
                />
                {Object.entries(tool.probe ?? {}).map(([platform, probe]) => (
                  <Tag key={platform} tone="mono">
                    {platform}: {probe}
                  </Tag>
                ))}
              </div>

              {Object.entries(tool.package ?? {}).map(([platform, pack]) => (
                <div
                  key={platform}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}
                >
                  <Tag>{platform}</Tag>
                  <Tag tone="mono">{pack.key}</Tag>
                  {/* The hash is what stops this being anything the server
                      points at, so it is shown rather than hidden away. */}
                  <Tag tone="mono">sha256 {pack.sha256.slice(0, 12)}…</Tag>
                  {pack.unpack && pack.unpack !== 'none' && <Tag>{pack.unpack}</Tag>}
                </div>
              ))}

              <span style={{ ...text.note, color: colour.inkMuted }}>
                {tool.used_by.length > 0
                  ? `Used by ${tool.used_by.join(', ')}.`
                  : 'No step uses this yet.'}
              </span>

              <div style={{ display: 'flex', gap: space.tight }}>
                <Button onClick={() => setEditingTool(tool)}>Edit</Button>
                <Button
                  tone="danger"
                  onClick={() => void removeTool(tool.name)}
                  disabled={tool.used_by.length > 0}
                  disabledReason={`${tool.used_by.join(', ')} ${tool.used_by.length === 1 ? 'uses' : 'use'} it`}
                >
                  Remove
                </Button>
              </div>
            </Card>
          ))}

          {(toolList.data?.tools ?? []).length === 0 && toolList.status !== 'loading' && (
            <Card>
              <span style={{ ...text.small, color: colour.inkSoft }}>
                No tools yet. A step that is a single command does not need one; a chain of tools
                does, so that a machine can find or fetch each of them.
              </span>
            </Card>
          )}
        </>
      )}

      {section === 'processes' && (
        <>
          {/* Editing one takes the whole section over rather than going in a
              dialog: a chain is tall, the checker's answers sit beside the
              entries they are about, and none of that survives a scrolling
              box inside a scrolling page. */}
          {editingProcess ? (
            <Card title={editingProcess.name ? `Edit ${editingProcess.name}` : 'Add a step'}>
              <ProcessEditor
                initial={editingProcess}
                tools={processList.data?.tools ?? []}
                platforms={processList.data?.platforms ?? []}
                placeholders={processList.data?.placeholders ?? []}
                onSave={(def) => void saveProcess(def)}
                onCancel={() => setEditingProcess(null)}
              />
            </Card>
          ) : (
            <>
              <Card>
                <span style={{ ...text.small, color: colour.inkSoft }}>
                  {processList.data?.about}
                </span>
                <Button
                  tone="primary"
                  onClick={() =>
                    setEditingProcess({
                      name: '',
                      label: '',
                      runnable: true,
                      chain: [],
                      used_by: [],
                      problems: []
                    })
                  }
                >
                  Add a step
                </Button>
              </Card>

              {(processList.data?.processes ?? []).map((def) => (
                <Card key={def.name} title={def.label || def.name} aside={def.name}>
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: space.tight, alignItems: 'center' }}
                  >
                    {/* Deliberately not a Status: Good, stale and the rest
                        are what Maestro says about a *result*, and borrowing
                        those words here would make a definition look like a
                        file somebody could rely on. */}
                    {def.runnable === false ? (
                      <Tag>recorded by Maestro, never run</Tag>
                    ) : (
                      <Tag>
                        {(def.chain ?? []).length > 1
                          ? `a chain of ${def.chain!.length} tools`
                          : 'one command'}
                      </Tag>
                    )}
                    {def.produces && <Tag>makes {def.produces}</Tag>}
                    {(def.chain ?? []).map((entry, i) => (
                      <Tag key={i} tone="mono">
                        {entry.tool}
                        {entry.blocking === false ? ' →' : ''}
                      </Tag>
                    ))}
                  </div>

                  {/* A definition that was fine when it was written and is not
                      any more — a tool taken out from under it — says so here,
                      rather than at the moment a run needs it. */}
                  {def.problems.map((problem, i) => (
                    <span
                      key={i}
                      style={{
                        ...text.note,
                        color: problem.fatal ? colour.staleInk : colour.inkMuted
                      }}
                    >
                      {problem.fatal ? 'Would stop a run: ' : 'Worth a look: '}
                      {problem.says}
                    </span>
                  ))}

                  <span style={{ ...text.note, color: colour.inkMuted }}>
                    {def.used_by.length > 0
                      ? `A step in ${def.used_by.join(', ')}.`
                      : 'No pipeline uses this yet.'}
                  </span>

                  <div style={{ display: 'flex', gap: space.tight }}>
                    <Button onClick={() => setEditingProcess(def)}>Edit</Button>
                    <Button
                      tone="danger"
                      onClick={() => void removeProcess(def.name)}
                      disabled={def.used_by.length > 0}
                      disabledReason={`${def.used_by.join(', ')} ${def.used_by.length === 1 ? 'uses' : 'use'} it`}
                    >
                      Remove
                    </Button>
                  </div>
                </Card>
              ))}

              {(processList.data?.processes ?? []).length === 0 &&
                processList.status !== 'loading' && (
                  <Card>
                    <span style={{ ...text.small, color: colour.inkSoft }}>
                      No steps defined. A pipeline&rsquo;s columns each name one of these, so a board
                      cannot run anything until they exist.
                    </span>
                  </Card>
                )}
            </>
          )}
        </>
      )}

      {addingMachine !== null && (
        <Dialog
          title="Add a machine"
          says="Maestro makes a key, and the installer on that machine spends it. The key is good once, only for the machine you name, and only for four hours."
          confirm="Make the key"
          confirmWhyNot={addingMachine.trim() === '' ? 'Name the machine first.' : undefined}
          onConfirm={() => void makeEnrolmentKey()}
          onClose={() => setAddingMachine(null)}
          busy={busy}
        >
          <Field label="What the machine is called">
            <TextInput
              value={addingMachine}
              onChange={setAddingMachine}
              placeholder="render-01"
            />
          </Field>
          <span style={{ ...text.note, color: colour.inkMuted }}>
            This becomes its queue name too, so it is worth getting right — it cannot be changed
            afterwards without enrolling the machine again.
          </span>
        </Dialog>
      )}

      {enrolment && (
        <Dialog
          title={`${enrolment.host} is ready to enrol`}
          says="Shown once. Maestro keeps only a hash of it, so copy it now — making another replaces this one."
          confirm="Done"
          onConfirm={() => setEnrolment(null)}
          onClose={() => setEnrolment(null)}
        >
          <Field label="Run this on the machine, from an elevated PowerShell">
            <TextArea
              value={`.\\install.ps1 -MachineName ${enrolment.host} \`
  -ServerUrl ${window.location.origin} \`
  -EnrolmentKey ${enrolment.key}`}
              onChange={() => undefined}
              rows={4}
            />
          </Field>
          <Field label="Or on macOS and Linux">
            <TextArea
              value={`sudo ./install.sh --host ${enrolment.host} \\\n  --server ${window.location.origin} \\\n  --key ${enrolment.key}`}
              onChange={() => undefined}
              rows={4}
            />
          </Field>
          <span style={{ ...text.note, color: colour.inkMuted }}>
            Good until {new Date(enrolment.expires_at).toLocaleString()}. The machine needs no AWS
            key and no password — it gets credentials from Maestro, scoped to its own queue.
          </span>
        </Dialog>
      )}

      {editingRoot && (
        <Dialog
          title={editingRoot._id ? `Edit ${editingRoot.name}` : 'Add a storage location'}
          says={kinds.data?.about}
          confirm={editingRoot._id ? 'Save' : 'Add it'}
          confirmWhyNot={(editingRoot.name ?? '').trim() === '' ? 'Give it a name first.' : undefined}
          onConfirm={() => void saveRoot()}
          onClose={() => setEditingRoot(null)}
          busy={busy}
        >
          <Field label="Name Maestro refers to it by">
            <TextInput
              value={editingRoot.name ?? ''}
              onChange={(name) => setEditingRoot({ ...editingRoot, name })}
              placeholder="arcfs_share"
            />
          </Field>

          {/* The kind decides every other box, and cannot change afterwards:
              a location that became a different kind would quietly invalidate
              every path recorded against it. */}
          {editingRoot._id ? (
            <span style={{ ...text.note, color: colour.inkMuted }}>
              A {editingRoot.kind.replace('_', ' ')} location. Add a new one rather than changing
              what this one is.
            </span>
          ) : (
            <Field label="What kind">
              <Select
                value={editingRoot.kind}
                onChange={(kind) => setEditingRoot({ ...editingRoot, kind, credential: {} })}
                options={(kinds.data?.kinds ?? []).map((k) => ({ value: k.key, label: k.says }))}
              />
            </Field>
          )}

          {(kinds.data?.kinds ?? [])
            .find((k) => k.key === editingRoot.kind)
            ?.needs.map((need) => (
              <Field key={need.name} label={need.says}>
                <TextInput
                  value={String((editingRoot as Record<string, unknown>)[need.name] ?? '')}
                  onChange={(v) => setEditingRoot({ ...editingRoot, [need.name]: v })}
                />
              </Field>
            ))}

          {((kinds.data?.credentials ?? {})[editingRoot.kind] ?? []).length > 0 && (
            <>
              <span style={{ ...text.label, color: colour.inkMuted }}>
                {editingRoot.secret_ref
                  ? 'Already stored. Type a new value only to replace it.'
                  : 'Kept in AWS Secrets Manager, never in the database.'}
                {editingRoot.kind === 's3' &&
                  ' Leave these empty for a bucket in this AWS account — the machine uses the role it already has.'}
              </span>
              {((kinds.data?.credentials ?? {})[editingRoot.kind] ?? []).map((field) => (
                <Field key={field.name} label={field.says}>
                  <TextInput
                    value={editingRoot.credential[field.name] ?? ''}
                    onChange={(v) =>
                      setEditingRoot({
                        ...editingRoot,
                        credential: { ...editingRoot.credential, [field.name]: v }
                      })
                    }
                    {...(field.secret ? { type: 'password' as const } : {})}
                    {...(editingRoot.secret_ref ? { placeholder: 'unchanged' } : {})}
                  />
                </Field>
              ))}
            </>
          )}
        </Dialog>
      )}

      {inviting && (
        <Dialog
          title="Add somebody"
          says="Maestro creates their account and emails them a temporary password from invites@maestro.stoa.haus. They choose their own the first time they sign in."
          confirm="Send the invitation"
          confirmWhyNot={
            !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(inviting.email.trim())
              ? 'An email address first — it is how they sign in.'
              : undefined
          }
          onConfirm={() => void sendInvite()}
          onClose={() => setInviting(null)}
          busy={busy}
        >
          <Field label="Email">
            <TextInput
              value={inviting.email}
              onChange={(email) => setInviting({ ...inviting, email })}
              placeholder="someone@arcturus.studio"
              type="email"
            />
          </Field>
          <Field label="Name (optional)">
            <TextInput
              value={inviting.name}
              onChange={(name) => setInviting({ ...inviting, name })}
              placeholder="Kcross"
            />
          </Field>
          <Field label="Role">
            <Select
              value={inviting.role}
              onChange={(role) =>
                setInviting({
                  ...inviting,
                  role,
                  // A viewer can never mark results good (rule 11), so the
                  // switch does not stay quietly ticked underneath.
                  can_mark_good: role === 'viewer' ? false : inviting.can_mark_good
                })
              }
              options={(users.data?.roles ?? []).map((r) => ({ value: r.key, label: r.says }))}
            />
          </Field>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, ...text.small }}>
            <input
              type="checkbox"
              checked={inviting.can_mark_good}
              disabled={inviting.role === 'viewer'}
              onChange={(e) => setInviting({ ...inviting, can_mark_good: e.target.checked })}
              style={{ width: 20, height: 20 }}
            />
            Can mark results good
          </label>
        </Dialog>
      )}

      {editingTool && (
        <Dialog
          title={editingTool.name ? `Edit ${editingTool.name}` : 'Add a tool'}
          says="A tool Maestro ships is downloaded onto every machine that needs it and run there. The SHA-256 is what stops that being anything the server points at — the agent checks it before unpacking and refuses a mismatch."
          confirm="Save"
          onConfirm={() => void saveTool(editingTool)}
          onClose={() => setEditingTool(null)}
        >
          <Field label="Name a pipeline refers to">
            <TextInput
              value={editingTool.name}
              onChange={(name) => setEditingTool({ ...editingTool, name })}
              placeholder="ffmpeg"
            />
          </Field>
          <Field label="What to call it on screen">
            <TextInput
              value={editingTool.label}
              onChange={(label) => setEditingTool({ ...editingTool, label })}
              placeholder="ffmpeg"
            />
          </Field>
          <Field label="How it gets onto a machine">
            <Select
              value={editingTool.install}
              onChange={(install) =>
                setEditingTool({ ...editingTool, install: install as 'pull' | 'manual' })
              }
              options={[
                { value: 'manual', label: 'A person installs it (Unreal, and anything large)' },
                { value: 'pull', label: 'Maestro ships it to the machine' }
              ]}
            />
          </Field>

          {(toolList.data?.platforms ?? ['windows', 'macos', 'linux']).map((platform) => (
            <div key={platform} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ ...text.smallStrong, color: colour.inkSoft }}>{platform}</span>
              <Field label="How to check whether it is already there">
                <TextInput
                  value={editingTool.probe?.[platform] ?? ''}
                  onChange={(probe) =>
                    setEditingTool({
                      ...editingTool,
                      probe: { ...editingTool.probe, [platform]: probe }
                    })
                  }
                  placeholder="ffmpeg -version"
                />
              </Field>
              {editingTool.install === 'pull' && (
                <>
                  <Field label="The file in the tools bucket">
                    <TextInput
                      value={editingTool.package?.[platform]?.key ?? ''}
                      onChange={(key) =>
                        setEditingTool({
                          ...editingTool,
                          package: {
                            ...editingTool.package,
                            [platform]: {
                              sha256: '',
                              ...editingTool.package?.[platform],
                              key
                            }
                          }
                        })
                      }
                      placeholder="tools/ffmpeg-8.1-win.zip"
                    />
                  </Field>
                  <Field label="Its SHA-256 (the agent refuses anything else)">
                    <TextInput
                      value={editingTool.package?.[platform]?.sha256 ?? ''}
                      onChange={(sha256) =>
                        setEditingTool({
                          ...editingTool,
                          package: {
                            ...editingTool.package,
                            [platform]: {
                              key: '',
                              ...editingTool.package?.[platform],
                              sha256
                            }
                          }
                        })
                      }
                      placeholder="64 hex characters"
                    />
                  </Field>
                </>
              )}
            </div>
          ))}

          {editingTool.install === 'manual' && (
            <Field label="What to tell somebody when it is missing">
              <TextInput
                value={editingTool.install_says ?? ''}
                onChange={(install_says) => setEditingTool({ ...editingTool, install_says })}
                placeholder="Install Unreal 5.4 from the Epic launcher and put UnrealEditor-Cmd.exe on the PATH."
              />
            </Field>
          )}
        </Dialog>
      )}

      {section === 'unreal' && <UnrealSection onSaid={setToast} />}

      {section === 'pipeline_defaults' && defaults.data && (
        <Card title="Pipeline defaults">
          <Field label={defaults.data.says.rerun_cap ?? 'Cap on automatic re-runs'}>
            <TextInput
              value={String(defaults.data.pipeline_defaults.rerun_cap)}
              onChange={(v) => Number(v) > 0 && void setDefault({ rerun_cap: Number(v) })}
            />
          </Field>
          <Field label={defaults.data.says.quiet_minutes_before_failed ?? 'Minutes a run may go quiet'}>
            <TextInput
              value={String(defaults.data.pipeline_defaults.quiet_minutes_before_failed)}
              onChange={(v) => Number(v) > 0 && void setDefault({ quiet_minutes_before_failed: Number(v) })}
            />
          </Field>
          <Field label={defaults.data.says.on_input_change ?? 'When an input changes'}>
            <Select
              value={defaults.data.pipeline_defaults.on_input_change}
              onChange={(on_input_change) => void setDefault({ on_input_change })}
              options={[
                { value: 'mark_stale', label: 'Mark it stale' },
                { value: 'rerun', label: 'Re-run it automatically' }
              ]}
            />
          </Field>
          <Field label={defaults.data.says.confirm_above_runs ?? 'Ask before launching more than'}>
            <TextInput
              value={String(defaults.data.confirm_above_runs)}
              onChange={(v) => Number(v) > 0 && void setDefault({ confirm_above_runs: Number(v) })}
            />
          </Field>
        </Card>
      )}

      {section === 'audit' && (
        <Card title="Audit log" aside="Read only, newest first, never removed.">
          {(audit.data?.entries ?? []).map((entry, i) => (
            <div
              key={`${entry.at}-${i}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                paddingTop: space.tight,
                borderTop: i === 0 ? 'none' : `1px solid ${colour.edgeSoft}`
              }}
            >
              <span style={text.small}>{entry.change}</span>
              <span style={{ ...text.note, color: colour.inkMuted }}>
                {entry.actor} · {entry.section} · {new Date(entry.at).toLocaleString()}
              </span>
            </div>
          ))}
          {(audit.data?.entries ?? []).length === 0 && (
            <span style={{ ...text.small, color: colour.inkSoft }}>Nothing has been changed yet.</span>
          )}
        </Card>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
